/**
 * Copy Presets — tenant/theme-level allowlists that constrain *which* copy
 * keys a theme is allowed to rewrite.
 *
 * Why presets exist (on top of the existing registry + validator):
 *   - The registry says "this key is overridable in principle." A preset says
 *     "this *specific tenant or theme* is only allowed to rewrite buttons and
 *     empty-states, not navigation items."
 *   - It gives tenant admins a coarse switch they actually understand
 *     ("Cosmetic only", "Buttons + headings", "Full creative") without making
 *     them pick keys one at a time.
 *   - It's the primary safety net for a theme uploaded by a partner: even if
 *     the JSON tries to rename "Devices" to "Steeds", the preset filter strips
 *     the disallowed key before it ever reaches the runtime, falling back to
 *     the canonical label.
 *
 * How it works:
 *   - Every CopyKey is tagged with a category (`button` / `heading` /
 *     `emptyState` / `navigation`). The category is derived from the key
 *     prefix so adding a new key to the registry slots it in automatically.
 *   - A preset is a Set of allowed categories + an optional explicit
 *     allow/deny list of individual keys for fine-tuning.
 *   - `filterOverridesForPreset()` runs at schema parse time and again
 *     defensively at read time; anything outside the allowlist is dropped.
 */
import {
  COPY_KEYS,
  COPY_REGISTRY,
  isCopyKey,
  type CopyKey,
} from "@/theme/copy/registry";
import type { LabelOverrides } from "@/theme/types";

export type CopyCategory =
  | "navigation"
  | "heading"
  | "button"
  | "emptyState";

/**
 * Map a registry key to its preset category. Driven by key prefix so the
 * registry stays the single source of truth — no second list to keep in sync.
 */
export function categoryOf(key: CopyKey): CopyCategory {
  if (key.startsWith("nav.")) return "navigation";
  if (key.startsWith("page.")) return "heading";
  if (key.startsWith("empty.")) return "emptyState";
  // Everything in `action.*` plus future button-y keys defaults to button.
  return "button";
}

export interface CopyPreset {
  id: string;
  name: string;
  description: string;
  /** Categories this preset allows to be rewritten. */
  categories: ReadonlyArray<CopyCategory>;
  /**
   * Optional explicit additions on top of `categories`. Keys listed here are
   * always allowed even if their category isn't included.
   */
  allowKeys?: ReadonlyArray<CopyKey>;
  /**
   * Optional explicit removals. Keys listed here are *never* allowed even if
   * their category is included. Lets us blacklist e.g. `action.save` while
   * still letting other buttons be themed.
   */
  denyKeys?: ReadonlyArray<CopyKey>;
}

/**
 * Built-in presets. Tenants pick one of these as their default; themes can
 * also declare a preset they were authored against, and the *stricter* of the
 * two wins at apply time.
 */
export const COPY_PRESETS = {
  /** No copy rewriting at all. The conservative default. */
  none: {
    id: "none",
    name: "Off",
    description:
      "Themes can change colors, fonts, and assets but never rewrite labels.",
    categories: [],
  },
  /** Empty-state CTAs only — safest creative outlet. */
  cosmeticOnly: {
    id: "cosmeticOnly",
    name: "Cosmetic only",
    description:
      "Themes may rewrite empty-state copy (zero-data CTAs). Navigation, buttons, and page titles stay canonical.",
    categories: ["emptyState"],
  },
  /** Headings + empty states — lets themes "set a tone" without touching CTAs. */
  headingsAndEmpty: {
    id: "headingsAndEmpty",
    name: "Headings & empty states",
    description:
      "Themes may rewrite page titles and empty-state CTAs. Action buttons and navigation remain canonical.",
    categories: ["heading", "emptyState"],
  },
  /** Buttons + headings + empty states. Excludes navigation to keep the IA legible. */
  buttonsAndHeadings: {
    id: "buttonsAndHeadings",
    name: "Buttons, headings & empty states",
    description:
      "Themes may rewrite CTAs, page titles, and empty-state copy. Sidebar navigation remains canonical for muscle memory.",
    categories: ["button", "heading", "emptyState"],
    // Save / Cancel are the two buttons users rely on most across every form;
    // even a "buttons allowed" preset shouldn't let a theme rename them.
    denyKeys: ["action.save", "action.cancel"],
  },
  /** Everything the registry permits. Power users / personal themes only. */
  full: {
    id: "full",
    name: "Full creative",
    description:
      "Themes may rewrite every label in the registry. Best for personal themes or strongly-branded tenant skins.",
    categories: ["navigation", "heading", "button", "emptyState"],
  },
} as const satisfies Record<string, CopyPreset>;

export type CopyPresetId = keyof typeof COPY_PRESETS;

export const COPY_PRESET_IDS = Object.keys(COPY_PRESETS) as CopyPresetId[];

export const DEFAULT_COPY_PRESET_ID: CopyPresetId = "buttonsAndHeadings";

export function getPreset(id: CopyPresetId | undefined | null): CopyPreset {
  if (id && id in COPY_PRESETS) return COPY_PRESETS[id];
  return COPY_PRESETS[DEFAULT_COPY_PRESET_ID];
}

export function isCopyPresetId(value: unknown): value is CopyPresetId {
  return typeof value === "string" && value in COPY_PRESETS;
}

/**
 * True if a single key is allowed under the given preset.
 */
export function isKeyAllowedByPreset(
  key: CopyKey,
  preset: CopyPreset,
): boolean {
  if (preset.denyKeys?.includes(key)) return false;
  if (preset.allowKeys?.includes(key)) return true;
  return preset.categories.includes(categoryOf(key));
}

export interface FilterResult {
  /** Overrides that survived the preset filter. */
  allowed: LabelOverrides;
  /** Keys that were dropped, with the reason — surfaced in the preview UI. */
  dropped: Array<{ key: CopyKey; reason: "category" | "deny" | "unknown" }>;
}

/**
 * Filter a `LabelOverrides` map down to only the keys the preset permits.
 * Dropped keys fall back to their canonical registry value at render time
 * (because `useCopy()` already does `overrides[key] ?? canonical`).
 */
export function filterOverridesForPreset(
  overrides: LabelOverrides | undefined,
  presetId: CopyPresetId | undefined | null,
): FilterResult {
  const preset = getPreset(presetId);
  const allowed: LabelOverrides = {};
  const dropped: FilterResult["dropped"] = [];
  if (!overrides) return { allowed, dropped };

  for (const [rawKey, value] of Object.entries(overrides)) {
    if (!isCopyKey(rawKey)) {
      dropped.push({ key: rawKey as CopyKey, reason: "unknown" });
      continue;
    }
    if (preset.denyKeys?.includes(rawKey)) {
      dropped.push({ key: rawKey, reason: "deny" });
      continue;
    }
    if (!isKeyAllowedByPreset(rawKey, preset)) {
      dropped.push({ key: rawKey, reason: "category" });
      continue;
    }
    if (typeof value === "string" && value.trim()) {
      allowed[rawKey] = value;
    }
  }
  return { allowed, dropped };
}

/**
 * Pick the stricter of two presets — used when a theme declares one preset
 * and the tenant declares another. "Stricter" means "allows fewer keys."
 */
export function intersectPresets(
  a: CopyPresetId | undefined | null,
  b: CopyPresetId | undefined | null,
): CopyPresetId {
  const presetA = getPreset(a);
  const presetB = getPreset(b);
  const countAllowed = (p: CopyPreset) =>
    COPY_KEYS.filter((k) => isKeyAllowedByPreset(k, p)).length;
  return countAllowed(presetA) <= countAllowed(presetB)
    ? (presetA.id as CopyPresetId)
    : (presetB.id as CopyPresetId);
}

/** Used by the registry/preview UI to show category badges. */
export const COPY_CATEGORY_LABELS: Record<CopyCategory, string> = {
  navigation: "Navigation",
  heading: "Headings",
  button: "Buttons",
  emptyState: "Empty states",
};

/** Sanity helper for previews — count keys per category in a preset. */
export function countAllowedKeys(presetId: CopyPresetId): number {
  const preset = getPreset(presetId);
  return COPY_KEYS.filter((k) => isKeyAllowedByPreset(k, preset)).length;
}

// Touch the registry so this module participates in the same dependency graph
// that the schema test walks — keeps an unused-symbol lint from removing it.
void COPY_REGISTRY;
